import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import { Event } from '../models/api/event';
import { EventsService } from '../providers/api/events.service';
import { ClientsService } from '../providers/api/clients.service';
import { Client } from '../models/api/client';
import { Objectives } from '../models/api/objectives';
import Timestamp = firebase.firestore.Timestamp;

@Injectable({ providedIn: 'root' })
export class ClientService {
  public clients: Client[] = [];
  public clientsObject: { [id: string]: Client } = {};
  public clients$: ReplaySubject<Client[]> = new ReplaySubject<Client[]>(0);
  public clientsObject$: ReplaySubject<{ [id: string]: Client }> = new ReplaySubject<{ [id: string]: Client }>(0);

  // Private
  private clientsObservable: AngularFirestoreCollection<Client>;
  private client: Client;
  private client$: ReplaySubject<Client> = new ReplaySubject<Client>(0);

  constructor(
    private firestore$: AngularFirestore,
    private events$: EventsService,
    private clientsApi$: ClientsService
  ) {
    this.clientsObservable = this.firestore$.collection<Client>('clients');
  }

  getAll(): Observable<Client[]> {
    // return this.clientsObservable.valueChanges({ idField: 'id' })
    return this.clientsApi$.clientsControllerFindAll()
      .pipe(
        tap((clients) => {
          const obClients = {};
          clients.forEach(fe => {
            if (fe.birthdate && (fe.birthdate instanceof Timestamp || moment.isMoment(fe.birthdate))) {
              fe.birthdate = fe.birthdate.toDate();
            } else if (!fe.birthdate) {
              delete fe.birthdate;
            }
            if (fe.tutorIds && fe.tutorIds.length > 0) {
              fe.tutorData = fe.tutorIds.map(m => {
                const client = (clients as Client[]).find(f => f.id === m.clientId);
                return { client, relationship: m.relationship };
              });
            }
            if (fe.billingIds) {
              fe.billingDatas = fe.billingIds.map(m => (clients as Client[]).find(f => f.id === m));
            }
            obClients[fe.id] = fe;
          });

          this.clients = clients;
          this.clientsObject = obClients;

          this.clients$.next(clients);
          this.clientsObject$.next(obClients);

          if (this.client) {
            this.getClientById(this.client.id);
          }
        })
      );
  }

  getClientById(id: string): Client {
    if (id) {
      this.client = this.clientsObject[id];
      this.client$.next(this.client);
      return this.clientsObject$[id];
    } else {
      delete this.client;
      this.client$.next(this.client);
      return this.client;
    }
  }

  getSelectedClient(): Client {
    return this.client;
  }

  getSelectedClient$(): Observable<Client> {
    return this.client$;
  }

  saveClient(client?: Client): Promise<void | string> {
    if (client) {
      return new Promise<string>(async resolve => {
        const clientId = client?.id ? client.id : this.firestore$.createId();

        delete client.tutorData;
        delete client.billingDatas;
        if (client.birthdate && (client.birthdate instanceof Timestamp || moment.isMoment(client.birthdate))) {
          client.birthdate = client.birthdate.toDate();
        } else if (!client.birthdate) {
          delete client.birthdate;
        }
        await this.firestore$.collection('clients').doc(clientId).set({ ...client });
        this.getAll().subscribe(() => resolve(clientId));
      });
    } else {
      return this.clientsObservable.doc(this.client.id).update(this.client);
    }
  }

  getEvents(): Observable<Event[]> {
    return new Observable<Event[]>(subscriber => {
      this.events$.eventsControllerFindWithFilter({
        clientsId: [ this.client.id ]
      })
        .subscribe(events => {
          subscriber.next(events);
          subscriber.complete();
        });
    });
  }

  updateEvent(event: Event): void {
    this.firestore$.collection<Event>('events').doc(event.id).update(event);
  }

  /**
   * Get tracing
   */
  getObjectives(): Objectives[] {
    return this.client.objectives;
  }

  updateObjectives(objectives: Objectives[]): void {
    this.client.objectives = objectives;
    this.saveClient(this.client);
  }

  deleteClient(client: Client): void {
    delete this.client;
    this.client$.next(this.client);
    this.clientsObservable.doc(client.id).delete();
  }
}
